import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { NumberInput } from "@/components/ui/number-input";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Trash2, ArrowLeft, Save, Printer } from "lucide-react";
import { toast } from "sonner";
import { ClientPicker } from "@/components/ClientPicker";

export const Route = createFileRoute("/tools/change-orders")({
  head: () => ({ meta: [{ title: "Change Orders" }, { name: "robots", content: "noindex" }] }),
  component: COPage,
});

type Line = { id: string; description: string; qty: number; price: number };
type CO = {
  id?: string; co_number?: number; project_id: string | null; client_id?: string | null;
  client_name: string | null; client_email: string | null;
  title: string; description: string | null;
  line_items: Line[]; total: number; status: string; signed_at: string | null;
};

const empty = (): CO => ({ project_id: null, client_name: "", client_email: "", title: "", description: "", line_items: [], total: 0, status: "draft", signed_at: null });

function COPage() {
  const [list, setList] = useState<CO[]>([]);
  const [current, setCurrent] = useState<CO | null>(null);
  const [projects, setProjects] = useState<{ id: string; title: string; client_name: string | null; client_email: string | null }[]>([]);

  const load = async () => {
    const { data } = await supabase.from("change_orders").select("*").order("created_at", { ascending: false });
    setList((data as any) || []);
    const { data: p } = await supabase.from("projects").select("id,title,client_name,client_email").order("created_at", { ascending: false });
    setProjects((p as any) || []);
  };
  useEffect(() => { load(); }, []);

  const total = useMemo(() => current?.line_items.reduce((s, l) => s + l.qty * l.price, 0) || 0, [current]);

  const save = async () => {
    if (!current || !current.title) return toast.error("Title required");
    const payload = { ...current, total };
    if (current.id) {
      const { error } = await supabase.from("change_orders").update(payload).eq("id", current.id);
      if (error) return toast.error(error.message);
    } else {
      const { error } = await supabase.from("change_orders").insert(payload as any);
      if (error) return toast.error(error.message);
    }
    toast.success("Saved"); setCurrent(null); load();
  };
  const del = async (id: string) => { if (confirm("Delete?")) { await supabase.from("change_orders").delete().eq("id", id); load(); } };

  const addLine = () => setCurrent(c => c && ({ ...c, line_items: [...c.line_items, { id: crypto.randomUUID(), description: "", qty: 1, price: 0 }] }));
  const updateLine = (i: number, patch: Partial<Line>) => setCurrent(c => c && ({ ...c, line_items: c.line_items.map((l, idx) => idx === i ? { ...l, ...patch } : l) }));
  const removeLine = (i: number) => setCurrent(c => c && ({ ...c, line_items: c.line_items.filter((_, idx) => idx !== i) }));

  const pickProject = (id: string) => {
    const p = projects.find(x => x.id === id);
    setCurrent(c => c && ({ ...c, project_id: id || null, client_name: p?.client_name || c.client_name, client_email: p?.client_email || c.client_email }));
  };

  if (!current) return (
    <div>
      <div className="flex items-center justify-between">
        <h1 className="font-display text-3xl font-bold">Change Orders</h1>
        <Button onClick={() => setCurrent(empty())}><Plus className="h-4 w-4 mr-1" />New CO</Button>
      </div>
      <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {list.map(co => (
          <div key={co.id} className="rounded-lg border border-border bg-card p-4">
            <div className="flex justify-between items-start">
              <div>
                <h3 className="font-semibold">CO-{co.co_number} · {co.title}</h3>
                <p className="text-sm text-muted-foreground">{co.client_name || "—"}</p>
                <p className="text-xs mt-1"><span className={`rounded-full px-2 py-0.5 ${co.status==='signed'?'bg-emerald-500/15 text-emerald-600':co.status==='sent'?'bg-amber-500/15 text-amber-600':'bg-muted'}`}>{co.status}</span> · ${co.total.toFixed(2)}</p>
              </div>
              <button onClick={() => del(co.id!)} className="text-destructive"><Trash2 className="h-4 w-4" /></button>
            </div>
            <Button variant="outline" size="sm" className="mt-3 w-full" onClick={() => setCurrent(co)}>Open</Button>
          </div>
        ))}
        {list.length === 0 && <p className="text-muted-foreground text-sm">No change orders yet.</p>}
      </div>
    </div>
  );

  return (
    <div>
      <div className="flex items-center justify-between print:hidden">
        <Button variant="ghost" onClick={() => setCurrent(null)}><ArrowLeft className="h-4 w-4 mr-1" />Back</Button>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => window.print()}><Printer className="h-4 w-4 mr-1" />Print</Button>
          <Button onClick={save}><Save className="h-4 w-4 mr-1" />Save</Button>
        </div>
      </div>
      <div className="mt-4 print:mt-0">
        {current.co_number && <p className="text-sm text-muted-foreground">CO-{current.co_number}</p>}
        <div className="grid gap-3 sm:grid-cols-2 print:hidden">
          <div><label className="text-xs text-muted-foreground">Project</label>
            <select className="w-full rounded border border-input bg-background p-2" value={current.project_id || ""} onChange={e => pickProject(e.target.value)}>
              <option value="">— None —</option>
              {projects.map(p => <option key={p.id} value={p.id}>{p.title}</option>)}
            </select>
          </div>
          <div><label className="text-xs text-muted-foreground">Status</label>
            <select className="w-full rounded border border-input bg-background p-2" value={current.status} onChange={e => setCurrent({ ...current, status: e.target.value })}>
              <option value="draft">Draft</option><option value="sent">Sent</option><option value="signed">Signed</option><option value="rejected">Rejected</option>
            </select>
          </div>
          <div className="sm:col-span-2"><ClientPicker clientId={current.client_id} clientName={current.client_name} onPick={(f) => setCurrent({ ...current, client_id: f.client_id, client_name: f.client_name, client_email: f.client_email })} /></div>
          <div><label className="text-xs text-muted-foreground">Client Name</label><Input value={current.client_name || ""} onChange={e => setCurrent({ ...current, client_name: e.target.value })} /></div>
          <div><label className="text-xs text-muted-foreground">Client Email</label><Input value={current.client_email || ""} onChange={e => setCurrent({ ...current, client_email: e.target.value })} /></div>
        </div>
        <div className="mt-3"><label className="text-xs text-muted-foreground print:hidden">Title</label><Input value={current.title} onChange={e => setCurrent({ ...current, title: e.target.value })} className="print:border-none print:text-xl print:font-bold print:p-0" /></div>
        <Textarea placeholder="Describe the scope change" className="mt-3" value={current.description || ""} onChange={e => setCurrent({ ...current, description: e.target.value })} />
        <div className="mt-4 flex items-center justify-between print:hidden">
          <h3 className="font-semibold">Line Items</h3>
          <Button size="sm" onClick={addLine}><Plus className="h-4 w-4" /></Button>
        </div>
        <table className="w-full mt-2 text-sm">
          <thead className="text-left text-muted-foreground border-b"><tr><th className="p-2">Description</th><th className="p-2 w-16">Qty</th><th className="p-2 w-24">Price</th><th className="p-2 w-24 text-right">Total</th><th className="print:hidden"></th></tr></thead>
          <tbody>
            {current.line_items.map((l, i) => (
              <tr key={l.id} className="border-b">
                <td className="p-1"><Input value={l.description} onChange={e => updateLine(i, { description: e.target.value })} /></td>
                <td className="p-1"><NumberInput value={l.qty} onValueChange={n => updateLine(i, { qty: n })} /></td>
                <td className="p-1"><NumberInput step="0.01" value={l.price} onValueChange={n => updateLine(i, { price: n })} /></td>
                <td className="p-1 text-right">${(l.qty * l.price).toFixed(2)}</td>
                <td className="p-1 print:hidden"><button onClick={() => removeLine(i)} className="text-destructive"><Trash2 className="h-4 w-4" /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="mt-4 text-right"><span className="text-muted-foreground">Total: </span><span className="font-display text-2xl font-bold">${total.toFixed(2)}</span></div>
        <div className="mt-8 grid grid-cols-2 gap-8 border-t pt-6">
          <div><p className="text-xs text-muted-foreground">Client Signature</p><div className="mt-8 border-b border-foreground/40" /></div>
          <div><p className="text-xs text-muted-foreground">Date</p><div className="mt-8 border-b border-foreground/40" /></div>
        </div>
      </div>
    </div>
  );
}
